import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, DocumentNode } from "@apollo/client";
import SideSheetModal from "@/components/SideSheetModal";
import TextField from "@/components/TextField";
import Select from "@/components/Select";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import { DateValueType } from "react-tailwindcss-datepicker";
import { NotePriorityValues, NotePriority, NoteNotable, NoteNotableValues, NoteVisibilityValues } from "@/types";
import type { CRMNoteReference } from "@/types";
import {
  useAlgoliaSearch,
  AlgoliaSearchResults,
  type PersonHit,
  createPersonOnlySearchUiState,
  createPersonOnlyHitRenderer,
  getPersonDisplayName,
  isPersonExactMatch
} from "@/components/AlgoliaSearch";
import ListItem from "@/components/ListItem/ListItem";
import Icon from "@/components/Icon";
import { formatNotePriority, dateValueTypeToISOString, dateStringToDateValueType } from "../NotesTable/noteHelpers";
import { GET_NOTE_QUERY } from "../NotesTable/queries";
import {
  CREATE_NOTE_MUTATION,
  UPDATE_NOTE_MUTATION,
  BULK_CREATE_REFERENCES_MUTATION,
  BULK_DELETE_REFERENCES_MUTATION,
  CreateNoteResponse,
  UpdateNoteResponse,
  BulkCreateReferencesResponse,
  BulkDeleteReferencesResponse,
  BackofficeCRMCreateNoteInput,
  BackofficeCRMUpdateNoteInput
} from "../NotesTable/mutations";
import AddEditNoteSideSheetSkeleton from "./AddEditNoteSideSheetSkeleton";
import ReferenceAdder from "./ReferenceAdder";
import Banner from "@/components/Banner";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary/ErrorBoundary";
import { showSnackbar, showErrorSnackbar } from "@/lib/snackbarUtils";
import FileUpload from "@/components/FileUpload/FileUpload";
import { useFlags } from "@/hooks/useFeatureFlags";
import { ObjectHeadingWrapper } from "@/features/person/components/PersonObjectHeading";
import { FacilityObjectHeadingWrapper } from "@/features/facility/components/FacilityObjectHeading";
import { EventObjectHeadingWrapper } from "@/features/event/components/EventObjectHeading";
import { useCanManageTasksBool } from "@/hooks/useCapabilities";
import AttachTaskSection from "@/components/AttachTaskSection";
import useAttachTask from "@/hooks/useAttachTask";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

export type PendingNoteValues = {
  title: string;
  content: string;
  priority: NotePriority;
  priorityExpirationDate?: string | null;
};

export type AddEditNoteSideSheetProps = {
  open: boolean;
  onClose: () => void;
  noteId?: string | null;
  notableId?: string;
  notableType?: NoteNotable;
  onSaveSuccess?: () => void;
  refetchQueries?: DocumentNode[];
  currentUserId?: string;
  currentUser?: { id: string; name: string; email: string; department?: string | null };
  pgaId?: string;
  eventSlug?: string;
  objectType?: string;
  showPersonSearch?: boolean;
  // In "capture" mode the side sheet collects values without persisting: it skips
  // mutations and hidden-in-capture sections, and hands values back via onCapture.
  mode?: "persist" | "capture";
  initialValues?: PendingNoteValues | null;
  onCapture?: (values: PendingNoteValues) => void;
};

export default function AddEditNoteSideSheet({
  open,
  onClose,
  noteId,
  notableId: notableIdProp,
  notableType: notableTypeProp,
  onSaveSuccess,
  refetchQueries = [],
  currentUserId,
  currentUser,
  pgaId: pgaIdProp,
  eventSlug: eventSlugProp,
  objectType = "person",
  showPersonSearch = false,
  mode = "persist",
  initialValues = null,
  onCapture
}: AddEditNoteSideSheetProps) {
  const isCaptureMode = mode === "capture";
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [priority, setPriority] = useState<NotePriority>(NotePriorityValues.NONE);
  const [expirationDate, setExpirationDate] = useState<DateValueType | string>("");
  const [errors, setErrors] = useState<{
    title?: string;
    content?: string;
    priority?: string;
    person?: string;
  }>({});
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [selectedReferences, setSelectedReferences] = useState<CRMNoteReference[]>([]);
  const [initialReferences, setInitialReferences] = useState<CRMNoteReference[]>([]);
  const [documentMode, setDocumentMode] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{ file: File; selected: boolean }[]>([]);
  const [uploadKey, setUploadKey] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [createdNoteId, setCreatedNoteId] = useState<string | null>(null);
  const [selectedPersonPgaId, setSelectedPersonPgaId] = useState<string | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<{
    name: string;
    profilePhoto?: string;
    facilityName?: string;
    sectionName?: string;
  } | null>(null);

  const [clearedPreSelection, setClearedPreSelection] = useState(false);

  const titleInputRef = useRef<HTMLInputElement>(null);
  const contentInputRef = useRef<HTMLInputElement>(null);

  const hasPreSelectedPerson = !!pgaIdProp && !clearedPreSelection;
  const hasPreSelectedNonPersonNotable =
    !pgaIdProp &&
    !!notableIdProp &&
    !!notableTypeProp &&
    notableTypeProp !== NoteNotableValues.PERSON &&
    !clearedPreSelection;
  const pgaId = selectedPersonPgaId || (hasPreSelectedPerson ? pgaIdProp : undefined);
  const notableId =
    selectedPersonPgaId || (hasPreSelectedPerson || hasPreSelectedNonPersonNotable ? notableIdProp || "" : "");
  const notableType = selectedPersonPgaId ? NoteNotableValues.PERSON : notableTypeProp || NoteNotableValues.PERSON;
  const needsPersonSelection =
    showPersonSearch && !hasPreSelectedPerson && !hasPreSelectedNonPersonNotable && !selectedPersonPgaId;
  const resolvedObjectType = selectedPersonPgaId ? "person" : objectType;

  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const isEventNoteContext = notableTypeProp === NoteNotableValues.EVENT || objectType === "event";

  const trackEventNoteAnalytics = (action: "create" | "update", noteIdForAnalytics?: string | null) => {
    if (!isEventNoteContext) return;
    trackEvent(
      action === "create" ? EVENT_ANALYTICS_EVENTS.noteCreated : EVENT_ANALYTICS_EVENTS.noteUpdated,
      getEventAnalyticsData({
        source: "notes",
        eventId: notableIdProp,
        eventSlug: eventSlugProp,
        noteId: noteIdForAnalytics || undefined,
        action
      })
    );
  };

  const personSearchIndexName = import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME;
  const {
    query: personQuery,
    displayValue: personDisplayValue,
    results: personResults,
    isLoading: personLoading,
    highlightedIndex,
    setHighlightedIndex,
    handleQueryChange: handlePersonQueryChange,
    handleClear: handleAlgoliaClear,
    handleItemSelected,
    setIsFocused: setPersonSearchFocused,
    setDisplayValue: setPersonDisplayValue,
    itemRefs,
    resultsContainerRef,
    searchBoxRef
  } = useAlgoliaSearch<PersonHit>({
    indexName: personSearchIndexName,
    hitsPerPage: 15,
    initialUiState: createPersonOnlySearchUiState(personSearchIndexName),
    getDisplayValue: getPersonDisplayName,
    isExactMatch: isPersonExactMatch,
    onSelect: (hit) => {
      const hitPgaId = hit.pga_id || hit.legacy_id;
      if (hitPgaId) {
        const fullName = getPersonDisplayName(hit);
        setSelectedPerson({
          name: fullName,
          profilePhoto: hit.profile_photo,
          facilityName: hit.facility_name,
          sectionName: hit.section_name
        });
        setSelectedPersonPgaId(hitPgaId);
        setErrors((prev) => ({ ...prev, person: undefined }));
        requestAnimationFrame(() => titleInputRef.current?.focus());
      }
    }
  });

  const hasActivePersonQuery = personQuery.trim() !== "";

  const handleClearPerson = useCallback(() => {
    handleAlgoliaClear();
    setPersonDisplayValue(undefined);
    setSelectedPerson(null);
    setSelectedPersonPgaId(null);
  }, [handleAlgoliaClear, setPersonDisplayValue]);

  const flags = useFlags();
  const referencesEnabled = flags["backoffice-person-note-references"];
  const canManageTasks = useCanManageTasksBool();

  const isEditMode = !isCaptureMode && !!noteId;
  const showAttachTask = !isCaptureMode && canManageTasks && !isEditMode;

  const { data, loading, error } = useQuery(GET_NOTE_QUERY, {
    variables: { id: noteId },
    skip: isCaptureMode || !noteId || !open
  });

  const note = data?.note;
  const isLoading = isEditMode && loading;

  const [createNote, { loading: createLoading }] = useMutation<CreateNoteResponse>(CREATE_NOTE_MUTATION, {
    refetchQueries
  });

  const [updateNote, { loading: updateLoading }] = useMutation<UpdateNoteResponse>(UPDATE_NOTE_MUTATION, {
    refetchQueries
  });

  const [bulkCreateReferences, { loading: bulkCreateLoading }] = useMutation<BulkCreateReferencesResponse>(
    BULK_CREATE_REFERENCES_MUTATION,
    {
      refetchQueries
    }
  );

  const [bulkDeleteReferences, { loading: bulkDeleteLoading }] = useMutation<BulkDeleteReferencesResponse>(
    BULK_DELETE_REFERENCES_MUTATION,
    {
      refetchQueries
    }
  );

  const task = useAttachTask(currentUser);
  const { reset: resetTask } = task;

  const isMutating = createLoading || updateLoading || bulkCreateLoading || bulkDeleteLoading;

  const canEditTitleAndContent = isEditMode
    ? note?.visibility !== NoteVisibilityValues.EXTERNAL && currentUserId === note?.author?.id
    : true;

  const priorityOptions = [
    {
      value: NotePriorityValues.NONE,
      label: formatNotePriority(NotePriorityValues.NONE)
    },
    {
      value: NotePriorityValues.PROMOTE,
      label: formatNotePriority(NotePriorityValues.PROMOTE)
    },
    {
      value: NotePriorityValues.ALERT,
      label: formatNotePriority(NotePriorityValues.ALERT)
    }
  ];

  const resetFormState = useCallback(() => {
    setTitle("");
    setContent("");
    setPriority(NotePriorityValues.NONE);
    setExpirationDate("");
    setErrors({});
    setMutationError(null);
    setSelectedReferences([]);
    setInitialReferences([]);
    setDocumentMode(false);
    setUploadedFiles([]);
    setUploadError(null);
    setCreatedNoteId(null);
    setClearedPreSelection(false);
    handleClearPerson();
    resetTask();
  }, [handleClearPerson, resetTask]);

  useEffect(() => {
    if (open) {
      if (isCaptureMode) {
        setTitle(initialValues?.title || "");
        setContent(initialValues?.content || "");
        setPriority(initialValues?.priority || NotePriorityValues.NONE);
        setExpirationDate(dateStringToDateValueType(initialValues?.priorityExpirationDate));
      } else {
        setTitle(note?.title || "");
        setContent(note?.content || "");
        setPriority(note?.priority || NotePriorityValues.NONE);
        setExpirationDate(dateStringToDateValueType(note?.priorityExpirationDate));
      }
      setErrors({});
      setMutationError(null);

      const noteRefs = isCaptureMode ? [] : (note?.references ?? []);
      setSelectedReferences(noteRefs);
      setInitialReferences(noteRefs);
      setDocumentMode(false);
      setUploadedFiles([]);
      setUploadError(null);
    } else {
      resetFormState();
    }
  }, [open, note, resetFormState, isCaptureMode, initialValues]);

  // Capture mode handles its own initial focus (SideSheetModal's is disabled below):
  // jump to content when the title is pre-filled and content is empty, otherwise title.
  useEffect(() => {
    if (!open || !isCaptureMode) return;
    const seededTitle = initialValues?.title?.trim();
    const seededContent = initialValues?.content?.trim();
    const focusTarget = seededTitle && !seededContent ? contentInputRef : titleInputRef;
    const timeoutId = setTimeout(() => focusTarget.current?.focus(), 100);
    return () => clearTimeout(timeoutId);
  }, [open, isCaptureMode, initialValues]);

  function setIsSideSheetOpen(isOpen: boolean) {
    if (!isOpen) {
      onClose();
    }
  }

  const saveReferences = async (noteId: string): Promise<string | null> => {
    const initialIds = new Set(initialReferences.map((r) => r.id));
    const currentIds = new Set(selectedReferences.map((r) => r.id));

    const toAdd = selectedReferences.filter((r) => !initialIds.has(r.id));
    const toRemove = initialReferences.filter((r) => !currentIds.has(r.id));

    if (toAdd.length === 0 && toRemove.length === 0) return null;

    const baseInput = {
      referencedById: noteId,
      referencedByType: "NOTE" as const
    };

    const toReferenceable = (ref: CRMNoteReference) => ({
      referenceableId: ref.id,
      referenceableType: "PERSON_PROFILE_DOCUMENT" as const
    });

    const [addSettled, removeSettled] = await Promise.allSettled([
      toAdd.length > 0
        ? bulkCreateReferences({
            variables: {
              input: { ...baseInput, references: toAdd.map(toReferenceable) }
            }
          })
        : null,
      toRemove.length > 0
        ? bulkDeleteReferences({
            variables: {
              input: {
                ...baseInput,
                references: toRemove.map(toReferenceable)
              }
            }
          })
        : null
    ]);

    const addPayload = addSettled.status === "fulfilled" ? addSettled.value?.data?.bulkCreateReferences : undefined;
    const removePayload =
      removeSettled.status === "fulfilled" ? removeSettled.value?.data?.bulkDeleteReferences : undefined;

    const succeededAddIds = new Set(addPayload?.successfulReferences?.map((r) => r.referenceableId) ?? []);
    const succeededRemoveIds = new Set(removePayload?.successfulReferences?.map((r) => r.referenceableId) ?? []);

    if (succeededAddIds.size > 0 || succeededRemoveIds.size > 0) {
      setInitialReferences((prev) => [
        ...prev.filter((r) => !succeededRemoveIds.has(r.id)),
        ...toAdd.filter((r) => succeededAddIds.has(r.id))
      ]);
    }

    const hasRejection = addSettled.status === "rejected" || removeSettled.status === "rejected";
    const failedAdd = addPayload?.failedReferences ?? [];
    const failedRemove = removePayload?.failedReferences ?? [];
    const hasPayloadFailure = failedAdd.length > 0 || failedRemove.length > 0;
    const addFailed = addSettled.status === "rejected" || (addPayload && !addPayload.success) || failedAdd.length > 0;
    const removeFailed =
      removeSettled.status === "rejected" || (removePayload && !removePayload.success) || failedRemove.length > 0;

    if (hasRejection || hasPayloadFailure || addFailed || removeFailed) {
      if (hasRejection) return "A network error occurred while saving references. Please try again.";
      const failedMessage = (addFailed && addPayload?.message) || (removeFailed && removePayload?.message);
      return failedMessage || "Some references could not be saved. Please try again.";
    }

    return null;
  };

  const handleFileUpload = (file: File) => {
    setUploadedFiles((prev) => [...prev, { file, selected: true }]);
    setUploadKey((k) => k + 1);
    setUploadError(null);
  };

  const handleFileSelectedChange = (index: number, selected: boolean) => {
    setUploadedFiles((prev) => prev.map((entry, i) => (i === index ? { ...entry, selected } : entry)));
  };

  const handleSave = async () => {
    setMutationError(null);

    if (isCaptureMode) {
      const newErrors: { title?: string; content?: string; priority?: string } = {};
      if (!title.trim()) newErrors.title = "Title is required";
      if (!content.trim()) newErrors.content = "Note content is required";
      if (!priority) newErrors.priority = "Priority is required";
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      onCapture?.({
        title: title.trim(),
        content: content.trim(),
        priority,
        priorityExpirationDate: dateValueTypeToISOString(expirationDate)
      });
      onClose();
      return;
    }

    // Retry path: note was already created but references failed — skip straight to refs.
    if (createdNoteId) {
      const refsError = await saveReferences(createdNoteId);
      if (!refsError) {
        showSnackbar("References saved");
        trackEventNoteAnalytics("create", createdNoteId);
        onSaveSuccess?.();
        onClose();
      } else {
        setMutationError(refsError);
      }
      return;
    }

    const newErrors: {
      title?: string;
      content?: string;
      priority?: string;
      person?: string;
    } = {};

    if (needsPersonSelection) {
      newErrors.person = "Person is required";
    }

    if (canEditTitleAndContent) {
      if (!title.trim()) {
        newErrors.title = "Title is required";
      }
      if (!content.trim()) {
        newErrors.content = "Note content is required";
      }
    }
    if (!priority) {
      newErrors.priority = "Priority is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (task.attachTask && !task.validate()) {
      return;
    }

    if (!notableId) {
      setMutationError("Unable to create note: no person or object selected.");
      return;
    }

    const expirationDateValue = dateValueTypeToISOString(expirationDate);

    try {
      if (isEditMode) {
        const input: BackofficeCRMUpdateNoteInput = {
          id: noteId!,
          priority
        };

        if (expirationDateValue) {
          input.priorityExpirationDate = expirationDateValue;
        }

        if (canEditTitleAndContent) {
          input.title = title.trim();
          input.content = content.trim();
        }

        const result = await updateNote({ variables: { input } });

        if (result.data?.updateNote?.success) {
          if (uploadedFiles.some((f) => f.selected)) {
            showErrorSnackbar("File upload is not yet supported. The note was saved without the attached files.");
          }

          const refsError = await saveReferences(noteId!);
          if (!refsError) {
            showSnackbar("Note updated");
            trackEventNoteAnalytics("update", noteId);
            onSaveSuccess?.();
            onClose();
          } else {
            setMutationError(refsError);
          }
        } else {
          setMutationError(result.data?.updateNote?.message || "Failed to update note");
        }
      } else {
        const input: BackofficeCRMCreateNoteInput = {
          notableId,
          notableType,
          title: title.trim(),
          content: content.trim(),
          priority: priority!
        };

        if (expirationDateValue) {
          input.priorityExpirationDate = expirationDateValue;
        }

        const defaultTaskName = title.trim() ? `Follow-up: ${title.trim()}` : "Follow-up to note";
        const taskInput = task.getTaskInput(defaultTaskName);
        if (taskInput) {
          input.task = taskInput;
        }

        const result = await createNote({ variables: { input } });

        if (result.data?.createNote?.success) {
          if (uploadedFiles.some((f) => f.selected)) {
            showErrorSnackbar("File upload is not yet supported. The note was saved without the attached files.");
          }

          const newNoteId = result.data.createNote.note?.id;

          if (newNoteId) {
            setCreatedNoteId(newNoteId);
          }

          let refsError: string | null = null;

          if (selectedReferences.length > 0) {
            if (newNoteId) {
              refsError = await saveReferences(newNoteId);
            } else {
              refsError = "Note was created but references could not be saved (missing note ID).";
            }
          }

          if (!refsError) {
            const personPgaId = pgaId;
            showSnackbar("Note created", {
              ...(personPgaId && {
                actions: [
                  <Button
                    key="view-notes"
                    variant="text"
                    onClick={() => navigate(`/people/${personPgaId}/person/notes`)}
                  >
                    View Notes
                  </Button>
                ]
              })
            });
            trackEventNoteAnalytics("create", newNoteId);
            const createdTaskId = result.data.createNote.task?.id;
            if (taskInput) {
              showSnackbar("Task created", {
                ...(createdTaskId && {
                  actions: [
                    <Button key="view-task" variant="text" onClick={() => navigate(`/tasks/${createdTaskId}`)}>
                      View Task
                    </Button>
                  ]
                })
              });
            }
            onSaveSuccess?.();
            onClose();
          } else {
            setMutationError(refsError);
          }
        } else {
          setMutationError(result.data?.createNote?.message || "Failed to create note");
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : `Error ${isEditMode ? "updating" : "creating"} note. Please try again.`;
      setMutationError(errorMessage);
    }
  };

  const handleCancel = () => {
    resetFormState();
    onClose();
  };

  const documentCount = selectedReferences.length + uploadedFiles.filter((f) => f.selected).length;

  const hasInitialCaptureValues = isCaptureMode && !!initialValues?.content?.trim();

  const noteTitle = documentMode ? "Add Documents" : isEditMode || hasInitialCaptureValues ? "Edit Note" : "Add Note";

  const noteActions = documentMode ? (
    <Button variant="outlined" onClick={() => setDocumentMode(false)}>
      Add to Note
    </Button>
  ) : (
    <>
      <Button variant="text" onClick={handleCancel} disabled={isLoading || isMutating}>
        Cancel
      </Button>
      <Button
        variant="filled"
        onClick={handleSave}
        disabled={isLoading || isMutating || needsPersonSelection}
        icon="check"
      >
        Save
      </Button>
    </>
  );

  return (
    <SideSheetModal
      isSidebarOpen={open}
      setSidebarOpen={setIsSideSheetOpen}
      title={noteTitle}
      goBack={documentMode ? () => setDocumentMode(false) : undefined}
      disableClose={documentMode}
      actions={noteActions}
      disableFocusManagement={isCaptureMode}
    >
      {documentMode && pgaId && (
        <div className="space-y-6 pt-6">
          <FileUpload
            key={uploadKey}
            label="Choose a file to upload"
            subtitle="Allowed file extensions: pdf, doc, docx, xls, xlsx, jpg, jpeg, png, txt"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
            handleUpload={handleFileUpload}
            errorMsg={uploadError ?? undefined}
          />

          <ReferenceAdder
            pgaId={pgaId}
            selectedReferences={selectedReferences}
            onSelectionChange={setSelectedReferences}
            uploadedFiles={uploadedFiles}
            onFileSelectedChange={handleFileSelectedChange}
          />
        </div>
      )}

      {!documentMode && (
        <>
          {isLoading ? (
            <AddEditNoteSideSheetSkeleton />
          ) : error ? (
            <ErrorBoundary message={error.message} variant="inline" />
          ) : isEditMode && !note ? (
            <EmptyState iconName="note" title="Note Not Found" />
          ) : (
            <div className={`space-y-6 ${hasPreSelectedPerson || hasPreSelectedNonPersonNotable ? "" : "mt-6"}`}>
              {!isCaptureMode && showPersonSearch && !hasPreSelectedPerson && !selectedPerson && (
                <>
                  <div ref={searchBoxRef}>
                    <TextField
                      label="Person"
                      placeholder="Search for a person"
                      value={personDisplayValue || personQuery}
                      onChangeText={(value) => {
                        setPersonDisplayValue(undefined);
                        handlePersonQueryChange(value);
                      }}
                      onFocus={() => setPersonSearchFocused(true)}
                      onBlur={() => setPersonSearchFocused(false)}
                      leadingIcon="search"
                      trailingIcon={
                        personDisplayValue || personQuery ? (
                          <button
                            className="cursor-pointer flex items-center h-full"
                            onClick={handleClearPerson}
                            aria-label="Clear search query"
                          >
                            <Icon name="cancel" />
                          </button>
                        ) : undefined
                      }
                      variant="outlined"
                      className="w-full"
                      supportingText="Search for a person to add a note"
                      error={!!errors.person}
                      errorText={errors.person}
                      data-testid="note-person-search-input"
                    />
                  </div>

                  {hasActivePersonQuery && (
                    <AlgoliaSearchResults
                      results={personResults}
                      query={personQuery}
                      isLoading={personLoading}
                      highlightedIndex={highlightedIndex}
                      setHighlightedIndex={setHighlightedIndex}
                      handleItemSelected={handleItemSelected}
                      itemRefs={itemRefs}
                      resultsContainerRef={resultsContainerRef}
                      renderHit={createPersonOnlyHitRenderer()}
                      isExactMatch={isPersonExactMatch}
                    />
                  )}
                </>
              )}

              {!isCaptureMode && showPersonSearch && !hasPreSelectedPerson && selectedPerson && (
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <ListItem
                      compact
                      useAvatar
                      picture={selectedPerson.profilePhoto}
                      headline={selectedPerson.name}
                      supportingText={
                        [selectedPerson.facilityName, selectedPerson.sectionName].filter(Boolean).join("\n") ||
                        undefined
                      }
                    />
                  </div>
                  <button
                    className="cursor-pointer flex items-center shrink-0 p-1 rounded-full hover:bg-sys-brand-surface-container-high transition-colors"
                    onClick={handleClearPerson}
                    aria-label="Remove selected person"
                    disabled={isMutating}
                  >
                    <Icon name="close" size={20} />
                  </button>
                </div>
              )}

              {!isCaptureMode && hasPreSelectedPerson && (
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <ObjectHeadingWrapper
                      pgaId={pgaIdProp!}
                      variant="condensed"
                      showMinimalMetadata={true}
                      hideTags={true}
                      avatarSize="medium"
                    />
                  </div>
                  {showPersonSearch && (
                    <button
                      className="cursor-pointer flex items-center shrink-0 p-1 rounded-full hover:bg-sys-brand-surface-container-high transition-colors"
                      onClick={() => {
                        setClearedPreSelection(true);
                        handleClearPerson();
                      }}
                      aria-label="Remove selected person"
                      disabled={isMutating}
                    >
                      <Icon name="close" size={20} />
                    </button>
                  )}
                </div>
              )}

              {!isCaptureMode && hasPreSelectedNonPersonNotable && notableTypeProp === NoteNotableValues.FACILITY && (
                <div className="flex-1 min-w-0">
                  <FacilityObjectHeadingWrapper
                    facilityId={notableIdProp!}
                    variant="condensed"
                    showMinimalMetadata={true}
                    avatarSize="medium"
                  />
                </div>
              )}

              {!isCaptureMode &&
                hasPreSelectedNonPersonNotable &&
                notableTypeProp === NoteNotableValues.EVENT &&
                eventSlugProp && (
                  <div className="flex-1 min-w-0">
                    <EventObjectHeadingWrapper
                      eventSlug={eventSlugProp}
                      variant="condensed"
                      showMinimalMetadata={true}
                      avatarSize="medium"
                    />
                  </div>
                )}

              {!needsPersonSelection && (
                <>
                  {mutationError && (
                    <Banner variant="error" description={mutationError} inline closable={false} hideSecondaryDismiss />
                  )}

                  {isEditMode && !canEditTitleAndContent && (
                    <Banner
                      variant="info"
                      description={
                        note?.visibility === NoteVisibilityValues.EXTERNAL
                          ? "This note was sent to someone outside this system and therefore cannot be edited. The priority can still be adjusted."
                          : "Only the original author can edit the content of this note. The priority can still be adjusted."
                      }
                      inline
                      closable={false}
                      hideSecondaryDismiss
                    />
                  )}

                  <TextField
                    ref={titleInputRef}
                    label="Title"
                    value={title}
                    onChangeText={setTitle}
                    required={canEditTitleAndContent}
                    disabled={!canEditTitleAndContent}
                    error={!!errors.title}
                    errorText={errors.title}
                    supportingText="Titles should be a few words summarizing the note content"
                    className="w-full"
                  />

                  <TextField
                    ref={contentInputRef}
                    label="Note Content"
                    type="textarea"
                    rows={6}
                    value={content}
                    onChangeText={setContent}
                    required={canEditTitleAndContent}
                    disabled={!canEditTitleAndContent}
                    error={!!errors.content}
                    errorText={errors.content}
                    className="w-full"
                  />

                  <Select
                    label="Priority"
                    options={priorityOptions}
                    value={priority ?? NotePriorityValues.NONE}
                    onSelect={(value) => setPriority((value || NotePriorityValues.NONE) as NotePriority)}
                    required
                    error={!!errors.priority}
                    errorText={errors.priority}
                    supportingText={`"Pinned" displays this note in the ${resolvedObjectType} header. "Pinned/Alert" also shows it each time this ${resolvedObjectType} is viewed.`}
                    className="w-full"
                  />

                  {priority && priority !== NotePriorityValues.NONE && (
                    <DateSelect
                      label="Priority Expiration Date"
                      value={expirationDate}
                      onChange={(date) => setExpirationDate(date || "")}
                      placeholder="Select a date"
                      useSingleDate
                      supportingText='On this date, the priority will be reset to "None"'
                      className="w-full"
                    />
                  )}

                  {!isCaptureMode && referencesEnabled && pgaId && (
                    <div
                      className="rounded-lg border border-sys-brand-outline-variant px-4 py-2"
                      data-testid="documents-summary-row"
                    >
                      <div className="flex items-center">
                        <div className="h-fit my-auto text-body-large-semibold text-sys-brand-on-surface-variant">
                          {documentCount === 0
                            ? "0 Documents Added"
                            : `${documentCount} ${documentCount === 1 ? "Document" : "Documents"}`}
                        </div>
                        <div className="ml-auto">
                          <Button
                            variant="text"
                            icon="add"
                            onClick={() => setDocumentMode(true)}
                            testId="add-reference-button"
                          >
                            Add Documents
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {showAttachTask && (
                    <AttachTaskSection
                      attachTask={task.attachTask}
                      onToggle={(enabled) => task.toggleAttachTask(enabled, `Follow-up: ${title}`)}
                      taskName={task.taskName}
                      onTaskNameChange={task.setTaskName}
                      taskAssignee={task.taskAssignee}
                      assigneeSearchQuery={task.taskAssigneeSearch}
                      onAssigneeSearchChange={task.setTaskAssigneeSearch}
                      selectAssignee={task.selectAssignee}
                      clearAssignee={task.clearAssignee}
                      assigneeResults={task.usersData}
                      assigneeLoading={task.usersLoading}
                      taskDueDate={task.taskDueDate}
                      onDueDateChange={task.setTaskDueDate}
                      taskIsPrivate={task.taskIsPrivate}
                      onTaskIsPrivateChange={task.setTaskIsPrivate}
                      errors={task.taskErrors}
                      clearError={task.clearError}
                      disabled={isMutating}
                      teamsEnabled={task.teamsEnabled}
                      selectedTeamTags={task.selectedTeamTags}
                      teamSearchTerm={task.teamSearchTerm}
                      onTeamSearchChange={task.setTeamSearchTerm}
                      availableDepartmentOptions={task.availableDepartmentOptions}
                      onTeamVisibilitySelect={task.handleTeamVisibilitySelect}
                      onRemoveTeamTag={task.removeTeamTag}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </SideSheetModal>
  );
}
